import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link, Stack, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { WorkflowActivityChangeSetField } from "../../../../../api/types/workflowTypes";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import type { InstructionSource } from "../../instructionQuoteHelper";
import { getInstructionSource } from "../../instructionQuoteHelper";
import WorkflowRunDetailsContext from "./WorkflowRunDetailsContext";

const getPromptParam = (source: InstructionSource, stepTemplateActivityId?: string): string | undefined => {
  if (source.location === "WorkflowInstructions") {
    return "workflow";
  }

  return source.location === "StepInstructions" ? stepTemplateActivityId : undefined;
};

interface InstructionSourceProps {
  source: InstructionSource;
}

interface InstructionsLinkProps extends InstructionSourceProps {
  quote: string;
}

const InstructionsLink = ({ source, quote }: InstructionsLinkProps) => {
  const { clientCode, hasPermissions } = useClientContext();
  const { currentTemplateVersionId, selectedActivity } = useDefinedContext(
    WorkflowRunDetailsContext,
    "WorkflowRunDetailsContext"
  );

  if (!hasPermissions(["ManageWorkflows"])) {
    return (
      <Typography variant="caption" sx={{ display: "block" }}>
        Only users who can manage workflows can change these instructions.
      </Typography>
    );
  }

  if (!currentTemplateVersionId) {
    return null;
  }

  const searchParams = new URLSearchParams({ tab: "instructions" });
  const prompt = getPromptParam(source, selectedActivity?.workflowActivityId);
  if (prompt) {
    searchParams.set("prompt", prompt);
  }

  const hashParams = new URLSearchParams({ quote });

  return (
    <Link
      component={RouterLink}
      to={`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowTemplates}/${currentTemplateVersionId}?${searchParams.toString()}#${hashParams.toString()}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ font: "inherit" }}
    >
      Open instructions
    </Link>
  );
};

const InstructionSourceDetails = ({ source }: InstructionSourceProps) => (
  <Stack spacing={1}>
    {source.quote && (
      <Stack spacing={0.5}>
        <Typography variant="caption">Instructions used for this result</Typography>
        <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}>
          {source.quote}
        </Typography>
      </Stack>
    )}
    {source.explanation && (
      <Stack spacing={0.5}>
        {!source.quote && <Typography variant="caption">AI explanation</Typography>}
        <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
          {source.explanation}
        </Typography>
      </Stack>
    )}
    {source.quote && <InstructionsLink source={source} quote={source.quote} />}
  </Stack>
);

interface Props {
  field: WorkflowActivityChangeSetField;
  hidden?: boolean;
}

const InstructionSourceInfoIcon = ({ field, hidden }: Props) => {
  const instructionSource = hidden ? undefined : getInstructionSource(field.metadata);
  if (!instructionSource) {
    return null;
  }

  return (
    <Tooltip
      arrow
      describeChild
      enterTouchDelay={0}
      leaveTouchDelay={60000}
      slotProps={{ tooltip: { sx: { maxHeight: "70vh", overflowY: "auto" } } }}
      title={<InstructionSourceDetails source={instructionSource} />}
    >
      <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
    </Tooltip>
  );
};

export default InstructionSourceInfoIcon;
